import { Fragment, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft, Calendar, XCircle, Trophy, Download,
  Check, X, Star, AlertTriangle, HelpCircle, Copy, Video, ExternalLink, FileText,
  Send, ChevronDown, ChevronRight, Eye, Paperclip, Brain, MoreHorizontal, Pencil, Trash2, RotateCcw,
  Loader2, Mic, Zap, Lock, Archive, ArchiveRestore, ShieldCheck,
} from 'lucide-react';
import {
  ApplicationRowProcessBadge,
  ScoreBadge, ChannelBadge, InterviewStatusBadge, InterviewSubstatusBadges, pickLatestInterview,
} from '@/components/shared/Badges';
import { isRecordingOnlyInterview } from '@/lib/pipelineRunInterviewMapper';
import PendingBillingBadge from '@/components/billing/PendingBillingBadge';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useTrialWriteGuard } from '@/hooks/useTrialWriteAccess';
import {
  canProcessPendingApplications,
  isApplicationAwaitingUpgrade,
  isApplicationBillingPipelineError,
  isApplicationReadyToProcess,
  resolveDisplayApplicationState,
  shouldHidePrimaryStatusBadge,
} from '@/lib/pendingProcessing';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatRelative, formatDateTime, formatDate, formatInterviewTableDateLines } from '@/lib/dateUtils';
import { renderInlineBold } from '@/lib/textUtils';
import type { AiCallProvenance, Application, Interview, PipelineDetailRun, SchedulingTimelineEntry } from '@/types';
import { toast } from 'sonner';
import { api } from '@/services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InterviewSchedulingModal from '@/components/InterviewSchedulingModal';
import { useApplicationDetail } from '@/hooks/useApplicationDetail';
import { cn } from '@/lib/utils';
import { useApplicationProcessQueue } from '@/hooks/useApplicationProcessQueue';
import { isApplicationInActiveBulkQueue } from '@/lib/bulkProcessQueueState';
import { FloatingAiChat } from '@/components/shared/FloatingAiChat';
import IdentityMismatchPanel from '@/components/applications/IdentityMismatchPanel';
import IdentityMismatchBadge from '@/components/applications/IdentityMismatchBadge';
import { resolveIdentityMismatch } from '@/lib/identityMismatch';
import type { ApplicationListReturnState } from '@/lib/listViewPersistence';

function interviewCreatedMs(int: Interview): number {
  if (!int.createdAt) return 0;
  const t = Date.parse(int.createdAt);
  return Number.isNaN(t) ? 0 : t;
}

function interviewScheduledMs(int: Interview): number {
  const t = Date.parse(int.scheduledAt);
  return Number.isNaN(t) ? 0 : t;
}

/** Fixed order: newest created first (same as previous default column sort). */
function sortInterviewsForTable(list: Interview[]): Interview[] {
  return [...list].sort((a, b) => {
    const c = interviewCreatedMs(b) - interviewCreatedMs(a);
    if (c !== 0) return c;
    const s = interviewScheduledMs(b) - interviewScheduledMs(a);
    if (s !== 0) return s;
    return a.id.localeCompare(b.id);
  });
}

const ApplicationDetailPage = () => {
  const { appId, posId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const goBackToList = useCallback(() => {
    const returnPath = (location.state as ApplicationListReturnState | null)?.listReturnPath;
    if (returnPath) {
      navigate(returnPath);
      return;
    }
    navigate(-1);
  }, [location.state, navigate]);

  const {
    app, setApp, notFound, positionAssignedMembers, screeningReports: reports, rawScreeningReports, cvContent, structuredData,
    pipelineDetail,
    companyId, isAwaiting, canReject, canHire, canWithdraw, canReactivate, hasInterviews, documents,
    rejectOpen, setRejectOpen, rejectNote, setRejectNote, rejectSendEmail, setRejectSendEmail, handleReject, rejectBusy,
    hireOpen, setHireOpen, hireNote, setHireNote, handleHire, hireBusy,
    withdrawOpen, setWithdrawOpen, withdrawNote, setWithdrawNote, withdrawBusy,
    reactivateOpen, setReactivateOpen, reactivateNote, setReactivateNote, handleReactivate,
    chatOpen, setChatOpen, chatMessages, chatInput, setChatInput, chatLoading, chatStreamStatuses, sendChat, resetChat,
    interviewOpen, setInterviewOpen, onInterviewScheduled, patchApplicationInListCaches,
    handleDownloadResume, handleDownloadDocument, handleViewResume, handleWithdraw,
  } = useApplicationDetail(appId, posId);

  const [interviewEditTarget, setInterviewEditTarget] = useState<Interview | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [cancelInterviewOpen, setCancelInterviewOpen] = useState(false);
  const [cancelInterviewTarget, setCancelInterviewTarget] = useState<Interview | null>(null);
  const [cancelInterviewNote, setCancelInterviewNote] = useState('');
  const [cancelSendGoogle, setCancelSendGoogle] = useState(true);
  const [cancelSendCustom, setCancelSendCustom] = useState(true);
  const [cancelInterviewSubmitting, setCancelInterviewSubmitting] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [joiningInterviewRunId, setJoiningInterviewRunId] = useState<string | null>(null);
  const [expandedInterviewId, setExpandedInterviewId] = useState<string | null>(null);
  const [processNowBusy, setProcessNowBusy] = useState(false);
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const { writeBlocked, guardWrite } = useTrialWriteGuard();

  const applicationsForQueue = useMemo(() => (app ? [app] : []), [app]);

  const refetchApplication = useCallback(async () => {
    if (!companyId || !app) return;
    const updated = await api.applications.get(companyId, app.positionId, app.id);
    if (updated) setApp(updated);
  }, [companyId, app, setApp]);

  const processQueue = useApplicationProcessQueue({
    companyId: companyId || undefined,
    applications: applicationsForQueue,
    refetchApplications: refetchApplication,
    onPatchApplications: (updater) => {
      setApp((prev) => {
        if (!prev) return prev;
        const next = updater([prev]);
        return next[0] ?? prev;
      });
    },
    t,
  });

  // BUG-09: a single "Process now" CTA covers both billing-blocked flows:
  //   - PIPELINE_ERROR + billing_limit_reached  (legacy: pipeline started then failed)
  //   - PENDING_UPGRADE                          (new: pipeline never started)
  const isPipelineErrorBilling = !!app && isApplicationBillingPipelineError(app);
  const isAwaitingUpgrade = !!app && (
    isApplicationAwaitingUpgrade(app, hasProcessingCredits) || isPipelineErrorBilling
  );
  const isReadyToProcess = !!app && isApplicationReadyToProcess(app, hasProcessingCredits);
  const canProcessNow = isReadyToProcess;

  const handleProcessNow = async () => {
    if (!app || processNowBusy || !guardWrite()) return;
    setProcessNowBusy(true);
    try {
      const updated = await api.applications.processNow(companyId, app.positionId, app.id);
      setApp(updated);
      toast.success(t('application.pendingBilling.processSuccess'));
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 402) {
        toast.error(t('application.pendingBilling.processBilling'));
        navigate('/profile?tab=billing');
      } else {
        toast.error(apiErr?.message || 'Process failed');
      }
    } finally {
      setProcessNowBusy(false);
    }
  };

  const sortedInterviews = useMemo(() => {
    if (!app?.interviews?.length) return [];
    return sortInterviewsForTable(app.interviews);
  }, [app?.interviews]);

  const data = app?.structuredData || (structuredData ?? undefined);
  const cvText = app?.cvContent || (cvContent ?? undefined);

  const identityMismatch = useMemo(
    () => (app ? resolveIdentityMismatch(app, data) : null),
    [app, data],
  );

  const openInterviewCreate = () => {
    setInterviewEditTarget(null);
    setInterviewOpen(true);
  };

  const openInterviewEdit = (int: Interview) => {
    setInterviewEditTarget(int);
    setInterviewOpen(true);
  };

  const onInterviewModalOpenChange = (v: boolean) => {
    setInterviewOpen(v);
    if (!v) setInterviewEditTarget(null);
  };

  const openCancelInterviewDialog = (int: Interview) => {
    setCancelInterviewTarget(int);
    setCancelInterviewNote('');
    const recordingOnly = isRecordingOnlyInterview(int);
    setCancelSendGoogle(!recordingOnly);
    setCancelSendCustom(false);
    setCancelInterviewOpen(true);
  };

  const cancelInterviewRecordingOnly = cancelInterviewTarget
    ? isRecordingOnlyInterview(cancelInterviewTarget)
    : false;

  const handleConfirmCancelInterview = async () => {
    if (!app || !cancelInterviewTarget || !guardWrite()) return;
    const recordingOnly = isRecordingOnlyInterview(cancelInterviewTarget);
    const sendCustom =
      !recordingOnly && (cancelSendCustom || cancelInterviewNote.trim().length > 0);
    if (!recordingOnly && !cancelSendGoogle && !sendCustom) {
      toast.error(t('appDetail.cancelInterviewChannelsRequired'));
      return;
    }
    const runId = cancelInterviewTarget.pipelineRunId ?? cancelInterviewTarget.id;
    setCancelInterviewSubmitting(true);
    try {
      const updated = await api.applications.cancelInterview(companyId, app.positionId, app.id, runId, {
        sendGoogleInvite: recordingOnly ? false : cancelSendGoogle,
        sendCustomEmail: sendCustom,
        cancellationMessage: recordingOnly ? undefined : (cancelInterviewNote.trim() || undefined),
      });
      setApp(updated);
      patchApplicationInListCaches(updated);
      setCancelInterviewOpen(false);
      setCancelInterviewTarget(null);
      setCancelInterviewNote('');
      onInterviewScheduled();
      toast.success(t('appDetail.interviewCancelled'));
    } catch {
      toast.error(t('appDetail.interviewCancelFailed'));
    } finally {
      setCancelInterviewSubmitting(false);
    }
  };

  const handleJoinRecallBotNow = async (int: Interview) => {
    if (!app || !guardWrite()) return;
    const runId = int.pipelineRunId ?? int.id;
    setJoiningInterviewRunId(runId);
    try {
      await api.applications.postInterviewTranscriptionJoinNow(companyId, app.positionId, app.id, runId);
      toast.success(t('appDetail.interviewJoinBotSuccess'));
      onInterviewScheduled();
    } catch (e) {
      const backend =
        e instanceof Error && e.message && !e.message.startsWith('Request failed with status')
          ? e.message
          : null;
      toast.error(backend ?? t('appDetail.interviewJoinBotFailed'));
    } finally {
      setJoiningInterviewRunId(null);
    }
  };

  // Sprint 7+ follow-up (21/05/26): explicit empty state when the
  // resolver pipeline has finished without locating the application.
  // Without this branch the page would sit on the skeleton below
  // forever — the reported "loader infinito" symptom. We give the
  // recruiter a clear back-out path instead.
  if (notFound) return (
    <div className="space-y-4">
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Link to="/applications" className="hover:text-foreground transition-colors">
          {t('appDetail.applications')}
        </Link>
      </nav>
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
          <AlertTriangle className="h-6 w-6 text-muted-foreground" />
        </div>
        <h2 className="text-base font-semibold mb-1">{t('appDetail.notFoundTitle')}</h2>
        <p className="text-sm text-muted-foreground max-w-sm mb-6">
          {t('appDetail.notFoundBody')}
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate('/applications')}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          {t('appDetail.backToList')}
        </Button>
      </div>
    </div>
  );

  if (!app) return (
    <div className="space-y-4">
      {/* Breadcrumb skeleton */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Skeleton className="h-4 w-24" />
        <span>/</span>
        <Skeleton className="h-4 w-40" />
      </nav>

      {/* Header skeleton */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-3">
        <div className="flex items-start gap-2 md:gap-3 md:flex-1 md:min-w-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goBackToList}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-6 w-48 max-w-full" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-5 w-28 rounded-full" />
              <Skeleton className="h-5 w-10 rounded-full" />
            </div>
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-full max-w-xs" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        </div>
        <Skeleton className="h-9 w-full md:w-28" />
      </div>

      {/* Tabs bar visible immediately with loading indicator */}
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <Tabs defaultValue="screening">
            <div className="sticky top-0 z-10 bg-background pb-2">
              <TabsList className="w-full overflow-x-auto justify-start">
                <TabsTrigger value="screening" disabled>{t('appDetail.tabScreening')}</TabsTrigger>
                <TabsTrigger value="resume" disabled>{t('appDetail.tabResume')}</TabsTrigger>
                <TabsTrigger value="documents" disabled>{t('appDetail.tabDocuments')}</TabsTrigger>
                <TabsTrigger value="interview" disabled>{t('appDetail.tabInterview')}</TabsTrigger>
                <TabsTrigger value="journey" disabled>{t('appDetail.tabJourney')}</TabsTrigger>
                <TabsTrigger value="history" disabled>{t('appDetail.tabHistory')}</TabsTrigger>
              </TabsList>
              {/* Sprint 7+ follow-up (21/05/26): the inline always-on
                  TopLoadingBar that lived here was redundant — the
                  shell-level <GlobalLoadingBar> in AppLayout is now
                  active via `useGlobalLoading('application-detail')`
                  for the whole resolve+fetch cycle. */}
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );

  const isArchived = app?.archived === true;

  const handleArchiveToggle = async () => {
    if (!app || !companyId || !guardWrite()) return;
    setArchiveBusy(true);
    try {
      const updated = isArchived
        ? await api.applications.unarchive(companyId, app.positionId, app.id)
        : await api.applications.archive(companyId, app.positionId, app.id);
      setApp(updated);
      toast.success(
        isArchived
          ? t('applications.bulkUnarchiveSuccess', { count: 1 })
          : t('applications.bulkArchiveSuccess', { count: 1 }),
      );
    } catch {
      toast.error(t('common.error'));
    } finally {
      setArchiveBusy(false);
    }
  };

  const actionButtons = [
    { label: t('appDetail.scheduleInterview'), icon: Calendar, color: 'text-primary', enabled: isAwaiting && !isArchived, disabledTip: 'Complete current step first', comingSoon: false, onClick: () => setTimeout(() => openInterviewCreate(), 0) },
    {
      label: isArchived ? t('applications.unarchiveAction') : t('applications.archiveAction'),
      icon: isArchived ? ArchiveRestore : Archive,
      color: 'text-muted-foreground',
      enabled: !archiveBusy,
      disabledTip: '',
      comingSoon: false,
      onClick: () => void handleArchiveToggle(),
    },
    { label: t('appDetail.reject'), icon: XCircle, color: 'text-destructive', enabled: canReject, disabledTip: 'Cannot reject in current state', comingSoon: false, onClick: () => setTimeout(() => setRejectOpen(true), 0) },
    ...(canWithdraw ? [{ label: t('appDetail.withdraw'), icon: X, color: 'text-warning', enabled: true, disabledTip: '', comingSoon: false, onClick: () => setTimeout(() => setWithdrawOpen(true), 0) }] : []),
    ...(canReactivate ? [{ label: t('appDetail.reactivate'), icon: RotateCcw, color: 'text-primary', enabled: true, disabledTip: '', comingSoon: false, onClick: () => setTimeout(() => setReactivateOpen(true), 0) }] : []),
    { label: t('appDetail.markHired'), icon: Trophy, color: 'text-success', enabled: canHire, disabledTip: 'Cannot mark as hired in current state', comingSoon: false, onClick: () => setTimeout(() => setHireOpen(true), 0) },
  ];

  return (
    <div className="space-y-4">
      {/* BUG-09: PENDING_UPGRADE banner — surfaces above the header so the
          recruiter understands why the AI report is empty. */}
      {isAwaitingUpgrade && app && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-700/60 dark:bg-amber-950/30">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 dark:text-amber-100">
              {t('application.pendingUpgrade.detailTitle')}
            </p>
            <p className="mt-1 text-amber-800/90 dark:text-amber-200/80">
              {t('application.pendingUpgrade.detailBody')}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/profile?tab=billing')}
            className="shrink-0 border-amber-700 text-amber-900 hover:bg-amber-100 dark:border-amber-300 dark:text-amber-100 dark:hover:bg-amber-900/40"
          >
            {t('application.pendingUpgrade.upgradeCta')}
          </Button>
        </div>
      )}
      {isArchived && app && (
        <div className="flex items-start gap-3 rounded-lg border border-muted-foreground/30 bg-muted/40 p-4 text-sm">
          <Archive className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <p className="flex-1 text-muted-foreground">{t('applications.archivedBanner')}</p>
        </div>
      )}
      {isReadyToProcess && app && !isArchived && (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm dark:border-emerald-700/60 dark:bg-emerald-950/30">
          <Zap className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300" />
          <div className="flex-1">
            <p className="font-semibold text-emerald-900 dark:text-emerald-100">
              {t('application.readyToProcess.detailTitle')}
            </p>
            <p className="mt-1 text-emerald-800/90 dark:text-emerald-200/80">
              {t('application.readyToProcess.detailBody')}
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleProcessNow}
            disabled={processNowBusy || writeBlocked}
            className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          >
            {processNowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {t('application.pendingBilling.processNow')}
          </Button>
        </div>
      )}

      {/* Breadcrumb */}
      {app && (
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/job-positions" className="hover:text-foreground transition-colors">{t('nav.positions')}</Link>
          <span>/</span>
          {posId ? (
            <Link to={`/job-positions/${posId}?tab=applications`} className="hover:text-foreground transition-colors">{app.positionTitle}</Link>
          ) : (
            <Link to={`/job-positions/${app.positionId}?tab=applications`} className="hover:text-foreground transition-colors">{app.positionTitle}</Link>
          )}
        </nav>
      )}

      {/* Header — stacked on mobile; inline actions from md */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2 md:gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goBackToList}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-lg font-semibold leading-tight md:text-xl">
              {app.candidateFirstName} {app.candidateLastName}
            </h2>
            <div className="flex flex-wrap items-center gap-1.5">
              {identityMismatch?.hasMismatch && (
                <IdentityMismatchBadge mismatch={identityMismatch} />
              )}
              {!shouldHidePrimaryStatusBadge(app, hasProcessingCredits) && (
                <ApplicationRowProcessBadge
                  state={resolveDisplayApplicationState(app.state, hasProcessingCredits)}
                  stopping={processQueue.isStopping(app.id)}
                  processing={processQueue.showsActiveProcessingBadge(app.id)}
                  queued={processQueue.showsQueuedInBulkBadge(app.id)}
                  stuck={processQueue.showsProcessingStuckBadge(app.id)}
                  hasProcessingCredits={hasProcessingCredits}
                />
              )}
              <PendingBillingBadge app={app} />
              <InterviewSubstatusBadges interviews={app.interviews} />
              <ScoreBadge score={app.aiScore} />
            </div>
            <div
              className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground"
              aria-label={t('applications.candidate')}
            >
              <a
                href={`mailto:${app.candidateEmail}`}
                className="min-w-0 max-w-full truncate hover:text-foreground transition-colors"
              >
                {app.candidateEmail}
              </a>
              {app.candidatePhone ? (
                <>
                  <span className="text-muted-foreground/40 select-none" aria-hidden>·</span>
                  <a
                    href={`tel:${app.candidatePhone}`}
                    className="shrink-0 whitespace-nowrap hover:text-foreground transition-colors"
                  >
                    {app.candidatePhone}
                  </a>
                </>
              ) : null}
              <span className="text-muted-foreground/40 select-none" aria-hidden>·</span>
              <span className="shrink-0 whitespace-nowrap">
                {t('appDetail.appliedOn')} {formatRelative(app.appliedAt)}
              </span>
              <span className="text-muted-foreground/40 select-none" aria-hidden>·</span>
              <span className="inline-flex shrink-0 items-center">
                <span className="sr-only">{t('applications.source')}</span>
                <ChannelBadge channel={app.channel} />
              </span>
              <span className="text-muted-foreground/40 select-none" aria-hidden>·</span>
              <span className="inline-flex min-w-0 max-w-full items-center">
                <span className="sr-only">{t('applications.position')}</span>
                <button
                  type="button"
                  className="min-w-0 max-w-[min(100%,12rem)] truncate text-left text-primary hover:underline sm:max-w-none"
                  onClick={() => navigate(`/job-positions/${app.positionId}`)}
                >
                  {app.positionTitle}
                </button>
              </span>
            </div>
          </div>
        </div>
        <div
          className={cn(
            'flex shrink-0 flex-col gap-2 border-t border-border/60 pt-3',
            'md:flex-row md:items-center md:border-0 md:pt-0',
          )}
        >
          {canProcessNow && (
            <Button
              size="sm"
              onClick={handleProcessNow}
              disabled={processNowBusy || writeBlocked}
              className="w-full gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 md:w-auto"
            >
              {processNowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
              {processNowBusy ? t('application.pendingBilling.processing') : t('application.pendingBilling.processNow')}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="w-full gap-1.5 md:w-auto">
                {t('common.actions')} <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actionButtons.map(btn => (
                <DropdownMenuItem
                  key={btn.label}
                  disabled={!btn.enabled}
                  className={`gap-2 ${btn.enabled ? btn.color : 'opacity-40'}`}
                  onClick={() => {
                    if (!btn.enabled) return;
                    if (!guardWrite()) return;
                    btn.onClick?.();
                  }}
                >
                  <btn.icon className="h-3.5 w-3.5" />
                  {btn.label}
                  {btn.comingSoon && <span className="text-[10px] bg-muted px-1 rounded ml-auto">{t('appDetail.comingSoon')}</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          <Tabs defaultValue="screening">
            <div className="pb-2">
              <div className="max-w-full overflow-x-auto">
                <TabsList className="inline-flex h-10 w-max min-w-0">
                  <TabsTrigger value="screening">{t('appDetail.tabScreening')}</TabsTrigger>
                  <TabsTrigger value="resume">{t('appDetail.tabResume')}</TabsTrigger>
                  <TabsTrigger value="documents">{t('appDetail.tabDocuments')}</TabsTrigger>
                  <TabsTrigger value="interview">{t('appDetail.tabInterview')}</TabsTrigger>
                  <TabsTrigger value="journey">{t('appDetail.tabJourney')}</TabsTrigger>
                  <TabsTrigger value="history">{t('appDetail.tabHistory')}</TabsTrigger>
                </TabsList>
              </div>
            </div>

            {/* Tab 1: AI Screening */}
            <TabsContent value="screening" className="mt-4 space-y-4">
              {rawScreeningReports === undefined && reports.length === 0 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-16 w-16 rounded-full" />
                    <Card className="flex-1 border-border/50 bg-primary/5">
                      <CardContent className="py-4 space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-5/6" />
                        <Skeleton className="h-3 w-1/3 mt-2" />
                      </CardContent>
                    </Card>
                  </div>
                  <Card className="border-border/50">
                    <CardContent className="py-4 grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-4/5" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-3/4" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
              {rawScreeningReports !== undefined && (app.state === 'PIPELINE_RUNNING' || app.state === 'APPLIED_NEW') && reports.length === 0 && (
                <Card className="border-border/50"><CardContent className="py-12 text-center"><div className="animate-pulse text-primary text-lg mb-2">●</div><p className="text-muted-foreground">{t('appDetail.analyzing')}</p></CardContent></Card>
              )}
              {rawScreeningReports !== undefined && reports.length === 0 && app.state !== 'PIPELINE_RUNNING' && app.state !== 'APPLIED_NEW' && (
                <Card className="border-border/50"><CardContent className="py-12 text-center"><p className="text-muted-foreground">{t('appDetail.noReport')}</p></CardContent></Card>
              )}
              {rawScreeningReports !== undefined && reports.length === 0 && (
                <ApplicationConsentPanel app={app} />
              )}
              {reports.length > 0 && (() => {
                const latest = reports[reports.length - 1];
                const prev = reports.length > 1 ? reports[reports.length - 2] : null;
                const newStrengths = prev
                  ? latest.strengths.filter(s => {
                      const text = typeof s === 'string' ? s : s.text;
                      return !prev.strengths.some(ps => (typeof ps === 'string' ? ps : ps.text) === text);
                    })
                  : [];
                const newWeaknesses = prev
                  ? latest.weaknesses.filter(w => {
                      const text = typeof w === 'string' ? w : w.text;
                      return !prev.weaknesses.some(pw => (typeof pw === 'string' ? pw : pw.text) === text);
                    })
                  : [];
                return (
                  <>
                    {/* Score + Summary */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="flex flex-col items-center gap-1">
                        <ScoreBadge score={latest.overallScore} size="lg" />
                        {latest.scoreDelta !== undefined && latest.scoreDelta !== 0 && (
                          <span className={`text-xs font-medium ${latest.scoreDelta > 0 ? 'text-success' : 'text-destructive'}`}>
                            {latest.scoreDelta > 0 ? '▲ +' : '▼ '}{Math.abs(latest.scoreDelta)}
                          </span>
                        )}
                      </div>
                      <Card className="flex-1 border-border/50 bg-primary/5">
                        <CardContent className="py-4">
                          <p className="text-sm">{renderInlineBold(latest.summary)}</p>
                          <p className="text-xs text-muted-foreground mt-2">{latest.triggerLabel} · {formatRelative(latest.generatedAt)}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Score trend */}
                    {reports.length > 1 && (
                      <Card className="border-border/50">
                        <CardContent className="py-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2">{t('appDetail.scoreHistory')}</p>
                          <div className="flex items-end gap-4">
                            {reports.map((r, i) => {
                              const isLatest = i === reports.length - 1;
                              return (
                                <div key={r.id} className={`flex flex-col items-center gap-0.5 ${isLatest ? 'font-semibold' : 'text-muted-foreground'}`}>
                                  <span className="text-lg">{r.overallScore}</span>
                                  {r.scoreDelta !== undefined && r.scoreDelta !== 0 && (
                                    <span className={`text-[10px] ${r.scoreDelta > 0 ? 'text-success' : 'text-destructive'}`}>
                                      {r.scoreDelta > 0 ? '+' : ''}{r.scoreDelta}
                                    </span>
                                  )}
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${isLatest ? 'bg-primary/10 text-primary' : 'bg-muted'}`}>
                                    v{r.version}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground">{r.triggerLabel}</span>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Analyzed documents badges */}
                    {latest.basedOnDocuments.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">{t('appDetail.analyzed')}:</span>
                        {latest.basedOnDocuments.map((doc, i) => (
                          <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-md">
                            {doc.label}
                          </span>
                        ))}
                      </div>
                    )}

                    <ApplicationConsentPanel app={app} />

                    {identityMismatch?.hasMismatch && (
                      <IdentityMismatchPanel mismatch={identityMismatch} />
                    )}

                    {/* Strengths / Weaknesses — 2 merged cards */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <Card className="border-border/50">
                        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-success"><Star className="h-4 w-4" /> {t('appDetail.strengths')}</CardTitle></CardHeader>
                        <CardContent className="space-y-1.5">
                          {[...latest.strengths]
                            .sort((a, b) => {
                              const ia = typeof a === 'string' ? 1 : a.importance;
                              const ib = typeof b === 'string' ? 1 : b.importance;
                              return ib - ia;
                            })
                            .map((s, i) => {
                              const isLegacy = typeof s === 'string';
                              const text = isLegacy ? s : s.text;
                              const importance = isLegacy ? null : s.importance;
                              const isNew = newStrengths.some(ns => (typeof ns === 'string' ? ns : ns.text) === text);
                              return (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                  {importance !== null && (
                                    <span className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${importance === 3 ? 'bg-success' : importance === 2 ? 'bg-yellow-400' : 'bg-muted-foreground/40'}`} title={`Importance: ${importance}`} />
                                  )}
                                  <span className="flex-1">{renderInlineBold(text)}</span>
                                  {isNew && <span className="text-[10px] bg-success/10 text-success px-1.5 rounded font-medium shrink-0">NEW</span>}
                                </div>
                              );
                            })}
                        </CardContent>
                      </Card>
                      <Card className="border-border/50">
                        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-destructive"><AlertTriangle className="h-4 w-4" /> {t('appDetail.weaknesses')}</CardTitle></CardHeader>
                        <CardContent className="space-y-1.5">
                          {[...latest.weaknesses]
                            .sort((a, b) => {
                              const ia = typeof a === 'string' ? 1 : a.importance;
                              const ib = typeof b === 'string' ? 1 : b.importance;
                              return ib - ia;
                            })
                            .map((w, i) => {
                              const isLegacy = typeof w === 'string';
                              const text = isLegacy ? w : w.text;
                              const importance = isLegacy ? null : w.importance;
                              const isNew = newWeaknesses.some(nw => (typeof nw === 'string' ? nw : nw.text) === text);
                              return (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                  {importance !== null && (
                                    <span className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${importance === 3 ? 'bg-destructive' : importance === 2 ? 'bg-yellow-400' : 'bg-muted-foreground/40'}`} title={`Importance: ${importance}`} />
                                  )}
                                  <span className="flex-1">{renderInlineBold(text)}</span>
                                  {isNew && <span className="text-[10px] bg-destructive/10 text-destructive px-1.5 rounded font-medium shrink-0">NEW</span>}
                                </div>
                              );
                            })}
                        </CardContent>
                      </Card>
                    </div>

                     {/* Missing Data */}
                     {latest.missingData.length > 0 && (
                       <Card className="border-border/50">
                         <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><HelpCircle className="h-4 w-4 text-warning" /> {t('appDetail.missingData')}</CardTitle></CardHeader>
                         <CardContent className="space-y-1.5">
                           {[...latest.missingData]
                             .sort((a, b) => {
                               const ia = typeof a === 'string' ? 2 : a.importance;
                               const ib = typeof b === 'string' ? 2 : b.importance;
                               return ib - ia;
                             })
                             .map((m, i) => {
                               const isLegacy = typeof m === 'string';
                               const text = isLegacy ? m : m.text;
                               const importance = isLegacy ? null : m.importance;
                               return (
                                 <div key={i} className="flex items-start gap-2 text-sm">
                                   <HelpCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
                                   {importance !== null && (
                                     <span className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${importance === 3 ? 'bg-destructive' : importance === 2 ? 'bg-yellow-400' : 'bg-muted-foreground/40'}`} title={`Importance: ${importance}`} />
                                   )}
                                   <span className="flex-1">{renderInlineBold(text)}</span>
                                 </div>
                               );
                             })}
                         </CardContent>
                       </Card>
                     )}

                    {/* Suggested Interview Questions */}
                    <Card className="border-border/50">
                      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><HelpCircle className="h-4 w-4 text-primary" /> {t('appDetail.suggestedQuestions')}</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        {latest.suggestedQuestions.map((q, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-md bg-primary/10 border border-primary/30 group">
                            <span className="text-xs text-primary font-semibold shrink-0 mt-0.5">{i + 1}.</span>
                             <p className="text-sm flex-1 text-foreground">{renderInlineBold(q)}</p>
                            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { navigator.clipboard.writeText(q); toast.success(t('appDetail.copied')); }}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {latest.aiProvenance && (
                      <ApplicationAiAuditPanel provenance={latest.aiProvenance} />
                    )}

                    {/* Screening History */}
                    {reports.length > 1 && (
                      <Collapsible>
                        <Card className="border-border/50">
                          <CollapsibleTrigger className="w-full">
                            <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                {t('appDetail.screeningHistory')} ({t('appDetail.versions', { count: String(reports.length) })})
                              </CardTitle>
                            </CardHeader>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent className="space-y-4">
                              {[...reports].reverse().slice(1).map((r) => (
                                <div key={r.id} className="border border-border/30 rounded-lg p-4 space-y-3">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                      <ScoreBadge score={r.overallScore} />
                                      {r.scoreDelta !== undefined && r.scoreDelta !== 0 && (
                                        <span className={`text-xs font-medium ${r.scoreDelta > 0 ? 'text-success' : 'text-destructive'}`}>
                                          {r.scoreDelta > 0 ? '▲ +' : '▼ '}{Math.abs(r.scoreDelta)}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium">v{r.version} — {r.triggerLabel}</p>
                                      <p className="text-xs text-muted-foreground">{formatRelative(r.generatedAt)}</p>
                                    </div>
                                  </div>
                                  {r.basedOnDocuments.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs text-muted-foreground">{t('appDetail.basedOn')}:</span>
                                      {r.basedOnDocuments.map((doc, i) => (
                                        <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-md">
                                          {doc.label}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                   <p className="text-sm text-muted-foreground">{renderInlineBold(r.summary)}</p>
                                   <div className="grid grid-cols-2 gap-3">
                                     <div>
                                       <p className="text-xs font-medium text-success mb-1">{t('appDetail.strengths')}</p>
                                       <div className="space-y-1">
                                         {[...r.strengths]
                                           .sort((a, b) => {
                                             const ia = typeof a === 'string' ? 1 : a.importance;
                                             const ib = typeof b === 'string' ? 1 : b.importance;
                                             return ib - ia;
                                           })
                                           .map((s, i) => {
                                             const text = typeof s === 'string' ? s : s.text;
                                             return (
                                               <div key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                                                 <Check className="h-3 w-3 text-success shrink-0 mt-0.5" />{renderInlineBold(text)}
                                               </div>
                                             );
                                           })}
                                       </div>
                                     </div>
                                     <div>
                                       <p className="text-xs font-medium text-destructive mb-1">{t('appDetail.weaknesses')}</p>
                                       <div className="space-y-1">
                                         {[...r.weaknesses]
                                           .sort((a, b) => {
                                             const ia = typeof a === 'string' ? 1 : a.importance;
                                             const ib = typeof b === 'string' ? 1 : b.importance;
                                             return ib - ia;
                                           })
                                           .map((w, i) => {
                                             const text = typeof w === 'string' ? w : w.text;
                                             return (
                                               <div key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                                                 <X className="h-3 w-3 text-destructive shrink-0 mt-0.5" />{renderInlineBold(text)}
                                               </div>
                                             );
                                           })}
                                       </div>
                                     </div>
                                   </div>
                                </div>
                              ))}
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* Tab 2: Resume */}
            <TabsContent value="resume" className="mt-4 space-y-4">
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={handleViewResume}>
                  <Eye className="h-3.5 w-3.5" /> {t('appDetail.viewResume')}
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={handleDownloadResume}>
                  <Download className="h-3.5 w-3.5" /> {t('appDetail.downloadResume')}
                </Button>
              </div>

              {data ? (
                <div className="space-y-4">
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">{t('appDetail.contact')}</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[['Email', app.candidateEmail || data.email], ['Phone', app.candidatePhone || data.phone], ['LinkedIn', data.linkedIn], ['GitHub', data.github], ['Portfolio', data.portfolio]].map(([k, v]) => v && (
                        <div key={k as string} className="min-w-0"><p className="text-xs text-muted-foreground">{k as string}</p><p className="text-sm font-medium break-all">{v as string}</p></div>
                      ))}
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">{t('appDetail.currentPosition')}</CardTitle></CardHeader>
                    <CardContent><p className="text-sm">{data.currentRole} @ {data.currentCompany}</p><p className="text-xs text-muted-foreground mt-1">{t('appDetail.yearsExp', { years: String(data.yearsOfExperience) })}</p></CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">{t('appDetail.experience')}</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {(data.experience || []).map((exp, i) => (
                        <div key={i} className="border-l-2 border-border pl-3">
                          <p className="text-sm font-medium">{exp.role}</p>
                          <p className="text-xs text-muted-foreground">{exp.company} · {exp.startDate} – {exp.endDate || t('appDetail.present')}</p>
                          {exp.description && <p className="text-xs text-muted-foreground mt-1">{exp.description}</p>}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">{t('appDetail.skills')}</CardTitle></CardHeader>
                    <CardContent className="flex flex-wrap gap-1.5">
                      {(data.skills || []).map(s => <span key={s} className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md">{s}</span>)}
                    </CardContent>
                  </Card>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="border-border/50">
                      <CardHeader className="pb-2"><CardTitle className="text-sm">{t('appDetail.education')}</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        {(data.education || []).map((ed, i) => (
                          <div key={i}><p className="text-sm font-medium">{ed.degree} {ed.field}</p><p className="text-xs text-muted-foreground">{ed.institution} · {ed.startDate} – {ed.endDate}</p></div>
                        ))}
                      </CardContent>
                    </Card>
                    <Card className="border-border/50">
                      <CardHeader className="pb-2"><CardTitle className="text-sm">{t('appDetail.languages')}</CardTitle></CardHeader>
                      <CardContent className="min-w-0 space-y-2 px-3 sm:px-6">
                        {(data.languages || []).map((l) => (
                          <div
                            key={l.name}
                            className="grid min-w-0 grid-cols-1 gap-x-3 gap-y-0.5 text-sm sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-start"
                          >
                            <span className="min-w-0 break-words font-medium">{l.name}</span>
                            <span className="min-w-0 break-words text-muted-foreground sm:text-right">
                              {l.proficiency}
                            </span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : structuredData === undefined && !app.structuredData ? (
                <div className="space-y-4 animate-fade-in">
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader>
                    <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="space-y-1.5">
                          <Skeleton className="h-3 w-16" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><Skeleton className="h-4 w-32" /></CardHeader>
                    <CardContent className="space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/3" />
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader>
                    <CardContent className="space-y-3">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="border-l-2 border-border pl-3 space-y-1.5">
                          <Skeleton className="h-4 w-1/2" />
                          <Skeleton className="h-3 w-2/3" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardHeader className="pb-2"><Skeleton className="h-4 w-20" /></CardHeader>
                    <CardContent className="flex flex-wrap gap-1.5">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-5 w-16 rounded-md" />
                      ))}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="border-border/50"><CardContent className="py-8 text-center text-muted-foreground">{t('appDetail.structuredPending')}</CardContent></Card>
              )}

              {/* Raw Resume Content — open by default */}
              <Collapsible defaultOpen>
                <Card className="border-border/50">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4" /> {t('appDetail.rawResume')}
                        <ChevronRight className="h-3.5 w-3.5 ml-auto transition-transform [[data-state=open]_&]:rotate-90" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="px-3 pb-4 pt-0 sm:px-4 md:px-6">
                      {cvText ? (
                        <div className="min-w-0 rounded-md border border-border/50 bg-muted/20 px-3 py-3 prose prose-sm max-w-none break-words dark:prose-invert sm:px-4 md:px-5 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_pre]:max-w-full [&_pre]:overflow-x-auto">
                          <ReactMarkdown>{cvText}</ReactMarkdown>
                        </div>
                      ) : cvContent === undefined && !app.cvContent ? (
                        <div className="space-y-2 animate-fade-in px-1 py-2">
                          <Skeleton className="h-3 w-full" />
                          <Skeleton className="h-3 w-11/12" />
                          <Skeleton className="h-3 w-10/12" />
                          <Skeleton className="h-3 w-9/12" />
                          <Skeleton className="h-3 w-full" />
                          <Skeleton className="h-3 w-8/12" />
                        </div>
                      ) : (
                        <p className="text-center text-sm text-muted-foreground py-6">{t('appDetail.resumePending')}</p>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            </TabsContent>

            {/* Tab 3: Documents */}
            <TabsContent value="documents" className="mt-4 space-y-4">
              <Card className="border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Paperclip className="h-4 w-4" /> {t('appDetail.documents')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 p-3 rounded-md border border-border/50 hover:bg-muted/30 transition-colors group">
                      <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">{doc.type} · {doc.size} · {formatRelative(doc.uploadedAt)}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDownloadDocument(doc.id, doc.name)}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 5: Interview */}
            <TabsContent value="interview" className="mt-4 space-y-4">
              {hasInterviews ? (
                <Card className="border-border/50 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">{t('appDetail.interviewColNumber')}</TableHead>
                        <TableHead className="min-w-[13rem] w-[14rem]">{t('appDetail.interviewColWhen')}</TableHead>
                        <TableHead className="w-[7.5rem] max-w-[7.5rem]">{t('appDetail.interviewColParticipants')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('appDetail.interviewColCreatedAt')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('appDetail.interviewColCreatedBy')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('appDetail.interviewColStatus')}</TableHead>
                        <TableHead className="whitespace-nowrap min-w-[120px]">{t('appDetail.interviewColRecallBot')}</TableHead>
                        <TableHead className="min-w-[6rem]">{t('appDetail.interviewColLinks')}</TableHead>
                        <TableHead className="w-[5.5rem] text-right pr-2" title={t('appDetail.interviewExpandHint')}>
                          <ChevronDown className="h-3.5 w-3.5 ml-auto text-muted-foreground opacity-70" aria-hidden />
                          <span className="sr-only">{t('appDetail.interviewColTranscript')}</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedInterviews.map((int, i) => {
                        /** #1 = oldest / lowest in list order; highest # = first row when sorted by Created desc (newest). */
                        const num = sortedInterviews.length - i;
                        const rowExpanded = expandedInterviewId === int.id;
                        const interviewColSpan = 9;
                        const whenLines = formatInterviewTableDateLines(int.scheduledAt);
                        const createdLines = formatInterviewTableDateLines(int.createdAt);
                        return (
                          <Fragment key={int.id}>
                            <TableRow className="align-top">
                              <TableCell className="font-medium text-muted-foreground">#{num}</TableCell>
                              <TableCell className="min-w-[13rem] max-w-[16rem] align-top">
                                {whenLines ? (
                                  <>
                                    <p className="text-sm text-foreground tabular-nums leading-snug">{whenLines.dateStr}</p>
                                    <p className="text-sm text-muted-foreground tabular-nums leading-snug">{whenLines.timeStr}</p>
                                    <p className="text-xs text-muted-foreground whitespace-normal break-words mt-0.5">
                                      {int.duration} min · {int.type}
                                    </p>
                                  </>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell className="w-[7.5rem] max-w-[7.5rem] align-top py-3">
                                {int.participants.length === 0 ? (
                                  <span className="text-xs text-muted-foreground">—</span>
                                ) : (
                                  <ul className="text-xs text-muted-foreground space-y-1 list-none m-0 p-0">
                                    {int.participants.map((p, idx) => (
                                      <li key={`${int.id}-p-${idx}`} className="truncate leading-tight" title={p.name}>
                                        {p.name}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </TableCell>
                              <TableCell className="align-top">
                                {createdLines ? (
                                  <>
                                    <p className="text-sm text-foreground tabular-nums leading-snug">{createdLines.dateStr}</p>
                                    <p className="text-sm text-muted-foreground tabular-nums leading-snug">{createdLines.timeStr}</p>
                                  </>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell className="align-top max-w-[8rem]">
                                <span className="text-sm break-words">{int.createdByName || '—'}</span>
                              </TableCell>
                              <TableCell className="align-top">
                                <InterviewStatusBadge status={int.status} />
                              </TableCell>
                              <TableCell className="align-top">
                                {int.status === 'Cancelled' || !int.meetLink ? (
                                  <span className="text-xs text-muted-foreground">—</span>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    className="h-8 gap-1.5 text-xs"
                                    disabled={joiningInterviewRunId === (int.pipelineRunId ?? int.id)}
                                    onClick={() => void handleJoinRecallBotNow(int)}
                                  >
                                    {joiningInterviewRunId === (int.pipelineRunId ?? int.id) ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                                    ) : (
                                      <Mic className="h-3.5 w-3.5 shrink-0" />
                                    )}
                                    {t('appDetail.interviewJoinBot')}
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell className="align-top">
                                <div className="flex flex-wrap items-center gap-2">
                                  {int.meetLink && (
                                    <a href={int.meetLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5 text-xs">
                                      <Video className="h-3 w-3 shrink-0" /> Meet
                                    </a>
                                  )}
                                  {int.calendarLink && (
                                    <a href={int.calendarLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 text-xs">
                                      <ExternalLink className="h-3 w-3 shrink-0" /> Calendar
                                    </a>
                                  )}
                                  {!int.meetLink && !int.calendarLink && <span className="text-xs text-muted-foreground">—</span>}
                                </div>
                              </TableCell>
                              <TableCell className="text-right align-top pr-2">
                                <div className="inline-flex items-center justify-end gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 shrink-0"
                                        aria-expanded={rowExpanded}
                                        onClick={() => setExpandedInterviewId(rowExpanded ? null : int.id)}
                                      >
                                        <ChevronDown
                                          className={`h-4 w-4 transition-transform ${rowExpanded ? 'rotate-180' : ''}`}
                                        />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      <p className="font-medium">{t('appDetail.interviewExpandHint')}</p>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {int.enableTranscription === true
                                          ? t('appDetail.interviewTranscriptOn')
                                          : int.enableTranscription === false
                                            ? t('appDetail.interviewTranscriptOff')
                                            : '—'}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                  {int.status === 'Cancelled' ? (
                                    <span className="inline-flex h-7 w-7 items-center justify-center text-xs text-muted-foreground">—</span>
                                  ) : (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 shrink-0"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openInterviewEdit(int);
                                          }}
                                        >
                                          <Pencil className="h-3.5 w-3.5 mr-2" />
                                          {t('appDetail.interviewEdit')}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openCancelInterviewDialog(int);
                                          }}
                                        >
                                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                                          {t('appDetail.interviewCancel')}
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                            {rowExpanded && (
                              <TableRow className="bg-muted/20 hover:bg-muted/25 border-0">
                                <TableCell colSpan={interviewColSpan} className="p-0 border-t border-border/50">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border/40">
                                    <div className="p-4 min-w-0">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                        {t('appDetail.transcript')}
                                      </p>
                                      <div className="max-h-[min(24rem,55vh)] overflow-y-auto text-sm text-muted-foreground whitespace-pre-wrap break-words pr-1">
                                        {int.transcript || t('appDetail.noTranscript')}
                                      </div>
                                    </div>
                                    <div className="p-4 min-w-0">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                        {t('appDetail.aiReport')}
                                      </p>
                                      <div className="max-h-[min(24rem,55vh)] overflow-y-auto text-sm text-muted-foreground whitespace-pre-wrap break-words pr-1">
                                        {int.reportSummary || t('appDetail.interviewNoAiReport')}
                                      </div>
                                    </div>
                                  </div>
                                  {int.notes ? (
                                    <div className="px-4 pb-4 pt-0 text-xs text-muted-foreground border-t border-border/30 bg-muted/10">
                                      <span className="font-medium text-foreground/80">{t('appDetail.interviewNotesLabel')}: </span>
                                      <span className="italic">{int.notes}</span>
                                    </div>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              ) : (
                <Card className="border-border/30 bg-muted/20">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Calendar className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm font-medium">{t('appDetail.noInterviews')}</p>
                    <p className="text-xs mt-1">{t('appDetail.noInterviewsHint')}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Tab 6: Journey */}
            <TabsContent value="journey" className="mt-4">
              <Card className="border-border/50">
                <CardContent className="pt-6">
                  <CandidateJourney app={app} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 7: History — unified chronological timeline */}
            <TabsContent value="history" className="mt-4">
              <Card className="border-border/50">
                <CardContent className="pt-5 pb-5 px-5">
                  {(() => {
                    // -------------------------------------------------------
                    // Helpers
                    // -------------------------------------------------------
                    const PIPELINE_LABEL: Record<string, string> = {
                      screening: 'AI Screening', interview: 'Interview',
                      interview_processing: 'Interview transcript & report',
                      offer: 'Offer', rejection: 'Rejection',
                      hire: 'Hire', withdrawal: 'Withdrawal',
                      offer_declined: 'Offer Declined',
                      rejection_pipeline: 'Rejection',
                    };
                    const EXECUTOR_LABEL: Record<string, string> = {
                      screening_cv: 'CV Analysis',
                      generate_report: 'AI Report Generated',
                      send_email_report: 'Report Email Sent',
                      send_rejection_email: 'Rejection Email Sent',
                      send_offer_email: 'Offer Email Sent',
                      schedule_interview: 'Interview Scheduled',
                    };
                    const activityLabel = (executorKey: string): string => {
                      if (executorKey === 'create_calendar_event') return t('appDetail.executorCreateCalendar');
                      if (executorKey === 'send_interview_invitation') return t('appDetail.executorSendInterviewInvite');
                      if (executorKey === 'ingest_stored_transcript') return t('appDetail.executorIngestTranscript');
                      if (executorKey === 'generate_interview_report') return t('appDetail.executorGenerateInterviewReport');
                      return EXECUTOR_LABEL[executorKey] ?? executorKey;
                    };
                    const tsToSeconds = (ts: any): number => {
                      if (!ts) return 0;
                      if (ts._seconds) return ts._seconds;
                      if (typeof ts === 'string') return new Date(ts).getTime() / 1000;
                      return 0;
                    };
                    const tsToISO = (ts: any): string | null => {
                      const s = tsToSeconds(ts);
                      return s ? new Date(s * 1000).toISOString() : null;
                    };
                    const formatBy = (by: string | undefined | null): string => {
                      if (!by || by === 'system' || by.startsWith('system:')) return 'System';
                      if (by === 'You' || by === 'Tu') return by;
                      if (by.length > 20) return 'Recruiter'; // Firebase UID
                      return by;
                    };

                    // -------------------------------------------------------
                    // Build flat timeline
                    // -------------------------------------------------------
                    type TLItem = {
                      id: string;
                      label: string;
                      detail?: string;
                      isoTs: string | null;
                      seconds: number;
                      by: string;
                      variant: 'ok' | 'fail' | 'info' | 'decision';
                      error?: string;
                      note?: string;
                    };

                    const items: TLItem[] = [];

                    if (pipelineDetail.length > 0) {
                      // Sort runs oldest-first
                      const sortedRuns = [...pipelineDetail].sort(
                        (a, b) => tsToSeconds(a.createdAt) - tsToSeconds(b.createdAt)
                      );

                      for (const run of sortedRuns) {
                        const runRow = run as PipelineDetailRun;
                        const runLabel = PIPELINE_LABEL[run.pipelineKey] ?? run.pipelineKey;
                        const by = formatBy(run.decidedBy);

                        // Run started — "Interview completed" here means pipeline scheduling finished, not the live interview
                        items.push({
                          id: `run-start-${run.id}`,
                          label: run.pipelineKey === 'interview'
                            ? t('appDetail.historyInterviewSchedulingStarted')
                            : `${runLabel} started`,
                          seconds: tsToSeconds(run.startedAt ?? run.createdAt),
                          isoTs: tsToISO(run.startedAt ?? run.createdAt),
                          by,
                          variant: 'info',
                        });

                        // Activities (already sorted by sequence from backend)
                        for (const step of run.steps) {
                          for (const act of step.activities) {
                            const actLabel = activityLabel(act.executorKey);
                            const actTs = act.completedAt ?? act.startedAt ?? act.createdAt;
                            items.push({
                              id: `act-${run.id}-${step.id}-${act.id}`,
                              label: actLabel,
                              detail: act.state === 'FAILED' ? 'Failed' : undefined,
                              seconds: tsToSeconds(actTs),
                              isoTs: tsToISO(actTs),
                              by: 'System',
                              variant: act.state === 'FAILED' ? 'fail' : 'ok',
                              error: act.error,
                            });
                          }
                        }

                        // Interview slot audit (append-only schedulingTimeline; legacy = single inferred row)
                        if (run.pipelineKey === 'interview') {
                          const tl = runRow.schedulingTimeline;
                          const anchorTs = run.completedAt ?? run.startedAt ?? run.createdAt;
                          const events: SchedulingTimelineEntry[] =
                            tl && tl.length > 0
                              ? [...tl].sort(
                                  (a, b) => tsToSeconds(a.recordedAt) - tsToSeconds(b.recordedAt)
                                )
                              : [];
                          for (let si = 0; si < events.length; si++) {
                            const ev = events[si]!;
                            const evTs = ev.recordedAt ?? anchorTs;
                            const who = formatBy(ev.recordedBy ?? run.decidedBy);
                            const label = (() => {
                              if (ev.kind === 'transcription_bot_cancelled') {
                                return t('appDetail.historyTranscriptionBotCancelled', {
                                  suffix: ev.botIdSuffix ? ` (${ev.botIdSuffix})` : '',
                                });
                              }
                              if (ev.kind === 'transcription_bot_scheduled') {
                                const meetingLabel = formatDateTime(ev.scheduledDate);
                                const joinLabel = ev.effectiveJoinAt
                                  ? formatDateTime(ev.effectiveJoinAt)
                                  : meetingLabel;
                                const diffMs =
                                  ev.effectiveJoinAt && ev.scheduledDate
                                    ? new Date(ev.effectiveJoinAt).getTime() -
                                      new Date(ev.scheduledDate).getTime()
                                    : 0;
                                if (ev.effectiveJoinAt && diffMs > 90_000) {
                                  return t('appDetail.historyTranscriptionBotScheduledLate', {
                                    meeting: meetingLabel,
                                    joinAt: joinLabel,
                                  });
                                }
                                return t('appDetail.historyTranscriptionBotScheduled', { joinAt: joinLabel });
                              }
                              if (ev.kind === 'rescheduled' && ev.previousScheduledDate) {
                                return t('appDetail.historyInterviewRescheduledFromTo', {
                                  date: formatDateTime(ev.scheduledDate),
                                  previousDate: formatDateTime(ev.previousScheduledDate),
                                });
                              }
                              return t('appDetail.historyInterviewScheduledFor', {
                                date: formatDateTime(ev.scheduledDate),
                              });
                            })();
                            items.push({
                              id: `sched-${run.id}-${si}-${tsToSeconds(evTs)}`,
                              label,
                              seconds: tsToSeconds(evTs),
                              isoTs: tsToISO(evTs),
                              by: who,
                              variant: 'info',
                            });
                          }
                        }

                        // Run finished (includes CANCELLED — e.g. interview cancelled by recruiter)
                        if (
                          run.completedAt ||
                          run.state === 'COMPLETED' ||
                          run.state === 'FAILED' ||
                          run.state === 'CANCELLED'
                        ) {
                          const endTs = run.completedAt ?? run.startedAt ?? run.createdAt;
                          const failed = run.outcome === 'failed' || run.state === 'FAILED';
                          const cancelled =
                            run.state === 'CANCELLED' || run.outcome === 'cancelled';
                          let endLabel: string;
                          if (failed) {
                            endLabel = `${runLabel} failed`;
                          } else if (cancelled) {
                            endLabel =
                              run.pipelineKey === 'interview'
                                ? t('appDetail.historyInterviewCancelledEvent')
                                : `${runLabel} cancelled`;
                          } else if (run.pipelineKey === 'interview') {
                            endLabel = t('appDetail.historyInterviewPipelineCompleted');
                          } else {
                            endLabel = `${runLabel} completed`;
                          }
                          const decisionNote =
                            typeof run.decisionInputs?.note === 'string' && run.decisionInputs.note.trim()
                              ? run.decisionInputs.note.trim()
                              : undefined;
                          items.push({
                            id: `run-end-${run.id}`,
                            label: endLabel,
                            seconds: tsToSeconds(endTs),
                            isoTs: tsToISO(endTs),
                            by: 'System',
                            variant: failed ? 'fail' : cancelled ? 'decision' : 'ok',
                            ...(['withdrawal', 'rejection', 'hire', 'offer_declined'].includes(run.pipelineKey) && decisionNote
                              ? { note: decisionNote }
                              : {}),
                          });
                        }
                      }

                      // Add user-triggered app.history entries (decisions, rejections, etc.)
                      for (const h of app.history) {
                        const isUserAction = h.triggeredBy && h.triggeredBy !== 'system' && !h.triggeredBy.startsWith('system:');
                        const isTerminal = ['REJECTED', 'HIRED', 'WITHDRAWN', 'OFFER_DECLINED'].includes(h.toState);
                        if (isUserAction || isTerminal) {
                          const hSec = new Date(h.timestamp).getTime() / 1000;
                          // Skip if already covered by pipelineDetail
                          const alreadyCovered = items.some(i => Math.abs(i.seconds - hSec) < 5 && i.by !== 'System');
                          if (!alreadyCovered) {
                            items.push({
                              id: `hist-${h.id}`,
                              label: h.event,
                              seconds: hSec,
                              isoTs: h.timestamp,
                              by: formatBy(h.triggeredBy),
                              variant: h.toState === 'REJECTED' || h.toState === 'OFFER_DECLINED' ? 'fail'
                                : h.toState === 'HIRED' ? 'ok'
                                : 'decision',
                              note: h.note,
                            });
                          }
                        }
                      }
                    } else {
                      // Fallback: just app.history
                      for (const h of app.history) {
                        items.push({
                          id: `hist-${h.id}`,
                          label: h.event,
                          seconds: new Date(h.timestamp).getTime() / 1000,
                          isoTs: h.timestamp,
                          by: formatBy(h.triggeredBy),
                          variant: h.toState === 'REJECTED' || h.toState === 'OFFER_DECLINED' ? 'fail'
                            : h.toState === 'HIRED' ? 'ok'
                            : h.triggeredBy && h.triggeredBy !== 'system' ? 'decision'
                            : 'info',
                          note: h.note,
                        });
                      }
                    }

                    // Sort chronologically, remove duplicates
                    items.sort((a, b) => a.seconds - b.seconds);

                    if (items.length === 0) {
                      return <p className="text-xs text-muted-foreground">{t('appDetail.noHistory')}</p>;
                    }

                    return (
                      <div>
                        {items.map((item, idx) => {
                          const dotColor = item.variant === 'fail' ? 'bg-destructive'
                            : item.variant === 'ok' ? 'bg-success'
                            : item.variant === 'decision' ? 'bg-primary'
                            : 'bg-muted-foreground/40';
                          const isLast = idx === items.length - 1;
                          return (
                            <div key={item.id} className="flex gap-3">
                              {/* Timeline spine */}
                              <div className="flex flex-col items-center shrink-0 w-4">
                                <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${dotColor}`} />
                                {!isLast && <div className="w-px flex-1 bg-border/50 mt-1" />}
                              </div>
                              {/* Content */}
                              <div className={`pb-3 ${isLast ? '' : ''}`}>
                                <p className="text-sm leading-snug font-medium">{item.label}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {item.isoTs ? formatDateTime(item.isoTs) : '—'}
                                  {' · '}
                                  {item.by}
                                </p>
                                {item.error && (
                                  <p className="mt-1 text-xs text-destructive bg-destructive/5 rounded px-2 py-0.5">{item.error}</p>
                                )}
                                {item.note && (
                                  <p className="mt-0.5 text-xs text-muted-foreground italic">"{item.note}"</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Floating AI Chat */}
        <FloatingAiChat
          open={chatOpen}
          onOpenChange={setChatOpen}
          expanded={chatExpanded}
          onExpandedChange={setChatExpanded}
          messages={chatMessages}
          loading={chatLoading}
          statusMessages={chatStreamStatuses}
          input={chatInput}
          onInputChange={setChatInput}
          onSend={(prompt) => {
            if (prompt) setChatInput(prompt);
            // Defer so the input state is set before send reads it
            setTimeout(() => sendChat(), 0);
          }}
          onReset={resetChat}
          contextLabel={t('aiChat.candidate.contextLabel')}
          placeholder={t('appDetail.askCandidate')}
          suggestedPrompts={[
            t('aiChat.candidate.prompt1'),
            t('aiChat.candidate.prompt2'),
            t('aiChat.candidate.prompt3'),
          ]}
        />
      </div>

      {/* Interview Scheduling */}
      <InterviewSchedulingModal
        open={interviewOpen}
        onOpenChange={onInterviewModalOpenChange}
        candidateName={`${app.candidateFirstName} ${app.candidateLastName}`}
        candidateEmail={app.candidateEmail}
        positionTitle={app.positionTitle}
        companyId={companyId}
        positionId={app.positionId}
        applicationId={app.id}
        assignedMemberIds={positionAssignedMembers}
        onScheduled={onInterviewScheduled}
        mode={interviewEditTarget ? 'edit' : 'create'}
        editInterview={interviewEditTarget}
      />

      {/* Cancel interview dialog */}
      <Dialog
        open={cancelInterviewOpen}
        onOpenChange={(open) => {
          setCancelInterviewOpen(open);
          if (!open) {
            setCancelInterviewTarget(null);
            setCancelInterviewNote('');
            setCancelSendGoogle(true);
            setCancelSendCustom(true);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('appDetail.cancelInterviewTitle')}</DialogTitle>
            <DialogDescription>{t('appDetail.cancelInterviewDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!cancelInterviewRecordingOnly && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cancel-interview-message">{t('appDetail.cancelInterviewMessageLabel')}</Label>
                  <Textarea
                    id="cancel-interview-message"
                    placeholder={t('appDetail.cancelInterviewMessagePlaceholder')}
                    value={cancelInterviewNote}
                    onChange={(e) => setCancelInterviewNote(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-3 rounded-md border border-border/60 bg-muted/20 px-3 py-3">
                  <p className="text-xs font-medium text-muted-foreground">{t('appDetail.cancelInterviewNotifyLabel')}</p>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="cancel-send-google"
                      checked={cancelSendGoogle}
                      onCheckedChange={(c) => setCancelSendGoogle(c === true)}
                    />
                    <Label htmlFor="cancel-send-google" className="text-sm font-normal leading-snug cursor-pointer">
                      {t('appDetail.cancelInterviewSendGoogle')}
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="cancel-send-email"
                      checked={cancelSendCustom}
                      onCheckedChange={(c) => setCancelSendCustom(c === true)}
                    />
                    <Label htmlFor="cancel-send-email" className="text-sm font-normal leading-snug cursor-pointer">
                      {t('appDetail.cancelInterviewSendEmail')}
                    </Label>
                  </div>
                </div>
              </>
            )}
            {cancelInterviewRecordingOnly && (
              <p className="text-sm text-muted-foreground">{t('appDetail.cancelRecordingOnlyHint')}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelInterviewOpen(false)}
              disabled={cancelInterviewSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmCancelInterview()}
              disabled={cancelInterviewSubmitting}
            >
              {cancelInterviewSubmitting ? t('appDetail.cancellingInterview') : t('appDetail.interviewCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog — Sprint 5 (S5.B): the recruiter can opt out of the
          candidate-facing rejection email by unchecking the box. The note
          (when present) is included verbatim in the email body. */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('appDetail.rejectCandidate')}</DialogTitle>
            <DialogDescription className="sr-only">{t('appDetail.rejectCandidate')}</DialogDescription>
          </DialogHeader>
          <Textarea placeholder={t('appDetail.rejectNote')} value={rejectNote} onChange={e => setRejectNote(e.target.value)} rows={3} />
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-primary"
              checked={rejectSendEmail}
              onChange={(e) => setRejectSendEmail(e.target.checked)}
            />
            <span>
              {t('appDetail.rejectSendEmail')}
              <span className="block text-[11px] text-muted-foreground/80">
                {t('appDetail.rejectSendEmailHint')}
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={rejectBusy}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => void handleReject()} disabled={rejectBusy}>
              {rejectBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('appDetail.confirmReject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hire Dialog */}
      <Dialog open={hireOpen} onOpenChange={setHireOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('appDetail.hireCandidate')}</DialogTitle>
            <DialogDescription className="sr-only">{t('appDetail.hireCandidate')}</DialogDescription>
          </DialogHeader>
          <Textarea placeholder={t('appDetail.hireNote')} value={hireNote} onChange={e => setHireNote(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setHireOpen(false)} disabled={hireBusy}>{t('common.cancel')}</Button>
            <Button
              className="bg-success text-success-foreground hover:bg-success/90"
              onClick={() => void handleHire()}
              disabled={hireBusy}
            >
              {hireBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('appDetail.confirmHire')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 5 (S5.G) — Withdraw confirmation. The withdraw action is
          irreversible (state → WITHDRAWN, terminal). Previously it fired
          on dropdown click without confirmation, which led to accidental
          touches on tablets. Now it mirrors the reject/hire UX. */}
      <Dialog open={withdrawOpen} onOpenChange={(open) => {
        setWithdrawOpen(open);
        if (!open) setWithdrawNote('');
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('appDetail.withdrawCandidate')}</DialogTitle>
            <DialogDescription>{t('appDetail.withdrawConfirm')}</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t('appDetail.withdrawNote')}
            value={withdrawNote}
            onChange={(e) => setWithdrawNote(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)} disabled={withdrawBusy}>{t('common.cancel')}</Button>
            <Button
              variant="default"
              className="bg-warning text-warning-foreground hover:bg-warning/90"
              onClick={() => void handleWithdraw()}
              disabled={withdrawBusy}
            >
              {withdrawBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('appDetail.confirmWithdraw')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('appDetail.reactivateCandidate')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('appDetail.reactivateConfirm')}</p>
          <div className="space-y-2">
            <Label htmlFor="reactivate-note">{t('appDetail.reactivateNote')}</Label>
            <Textarea
              id="reactivate-note"
              value={reactivateNote}
              onChange={(e) => setReactivateNote(e.target.value)}
              placeholder={t('appDetail.reactivateNotePlaceholder')}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={() => void handleReactivate()}>{t('appDetail.confirmReactivate')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

function ApplicationAiAuditPanel({ provenance }: { provenance: AiCallProvenance }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const rows = [
    { label: t('appDetail.aiAuditProfile'), value: provenance.profileName },
    { label: t('appDetail.aiAuditModel'), value: `${provenance.modelConfigured}${provenance.modelResolved !== provenance.modelConfigured ? ` → ${provenance.modelResolved}` : ''}` },
    { label: t('appDetail.aiAuditProvider'), value: provenance.provider },
    { label: t('appDetail.aiAuditPromptHash'), value: provenance.promptHash, mono: true },
    ...(provenance.inputDocumentHash
      ? [{ label: t('appDetail.aiAuditDocumentHash'), value: provenance.inputDocumentHash, mono: true }]
      : []),
    { label: t('appDetail.aiAuditGeneratedAt'), value: formatDateTime(provenance.generatedAt) },
  ] as const;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2.5">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left">
          <p className="text-xs font-medium text-foreground">{t('appDetail.aiAuditTitle')}</p>
          <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
            {!open && <span className="text-[11px]">{t('appDetail.aiAuditToggle')}</span>}
            <ChevronDown
              className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
              aria-hidden
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-1.5">
          {rows.map((row) => (
            <div key={row.label} className="text-xs">
              <span className="text-muted-foreground">{row.label}: </span>
              <span className={row.mono ? 'font-mono text-[10px] break-all text-foreground' : 'text-foreground'}>
                {row.value}
              </span>
            </div>
          ))}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function ApplicationConsentPanel({ app }: { app: Application }) {
  const { t } = useLanguage();

  const rows = [
    {
      key: 'gdpr',
      label: t('appDetail.consentGdpr'),
      granted: app.gdprConsent === true,
      timestamp: app.gdprConsentTimestamp,
    },
    {
      key: 'ai',
      label: t('appDetail.consentAi'),
      granted: app.aiConsent === true,
      timestamp: app.aiConsentTimestamp,
    },
  ] as const;

  return (
    <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2.5 space-y-2">
      <p className="text-xs font-medium text-foreground">{t('appDetail.consentTitle')}</p>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.key} className="flex items-start gap-2 text-xs">
            <ShieldCheck
              className={cn(
                'h-3.5 w-3.5 shrink-0 mt-0.5',
                row.granted ? 'text-success' : 'text-muted-foreground/40',
              )}
              aria-hidden
            />
            <div className="min-w-0">
              <span className="text-muted-foreground">{row.label}: </span>
              {row.granted && row.timestamp ? (
                <span className="text-foreground">
                  {t('appDetail.consentGranted')} · {formatDateTime(row.timestamp)}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {t('appDetail.consentNotRecorded')}
                  <span className="block text-[11px] text-muted-foreground/80 mt-0.5">
                    {t('appDetail.consentNotRecordedHint')}
                  </span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CandidateJourney = ({ app }: { app: Application }) => {
  const { t } = useLanguage();
  const primaryInterview =
    pickLatestInterview(app.interviews) ?? app.interviews[0];
  const interviewStageInfo =
    primaryInterview == null
      ? undefined
      : primaryInterview.status === 'Cancelled'
        ? t('appDetail.journeyInterviewCancelled', { date: formatDate(primaryInterview.scheduledAt) })
        : t('appDetail.journeyScheduled', { date: formatDate(primaryInterview.scheduledAt) });
  const stages = [
    { key: 'applied', label: t('appDetail.journeyApplied'), icon: '📝', reached: true, info: t('appDetail.journeyAppliedInfo', { channel: app.channel, date: formatDate(app.appliedAt) }) },
    { key: 'screening', label: t('appDetail.journeyScreening'), icon: '🤖', reached: !['APPLIED_NEW'].includes(app.state), info: app.aiScore ? t('appDetail.journeyScreeningScore', { score: String(app.aiScore) }) : t('appDetail.journeyPending') },
    { key: 'awaiting', label: t('appDetail.journeyAwaiting'), icon: '⏳', reached: ['AWAITING_DECISION', 'REJECTED', 'HIRED'].includes(app.state), info: app.state === 'AWAITING_DECISION' ? t('appDetail.journeyWaiting') : undefined },
    { key: 'interview', label: t('appDetail.journeyInterview'), icon: '📅', reached: app.interviews.length > 0, info: interviewStageInfo },
    { key: 'hired', label: app.state === 'REJECTED' ? t('appDetail.journeyRejected') : t('appDetail.journeyHired'), icon: app.state === 'REJECTED' ? '❌' : '🎉', reached: ['REJECTED', 'HIRED'].includes(app.state), info: undefined },
  ];

  let currentIdx = 0;
  for (let i = stages.length - 1; i >= 0; i--) { if (stages[i].reached) { currentIdx = i; break; } }

  return (
    <div className="space-y-0">
      {stages.map((stage, i) => (
        <div key={stage.key} className="flex gap-3 pb-0">
          <div className="flex flex-col items-center">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm ${
              i <= currentIdx
                ? (stage.key === 'hired' && app.state === 'REJECTED' ? 'bg-destructive/10' : i === currentIdx ? 'bg-primary/10 ring-2 ring-primary/30' : 'bg-success/10')
                : 'bg-muted'
            }`}>
              {i < currentIdx ? <Check className="h-4 w-4 text-success" /> : stage.icon}
            </div>
            {i < stages.length - 1 && <div className={`w-px h-8 ${i < currentIdx ? 'bg-success/30' : 'bg-border'}`} />}
          </div>
          <div className="pt-1.5 pb-4">
            <p className={`text-sm font-medium ${i > currentIdx ? 'text-muted-foreground' : ''}`}>{stage.label}</p>
            {stage.info && stage.reached && <p className="text-xs text-muted-foreground mt-0.5">{stage.info}</p>}
            {i === currentIdx && app.state === 'AWAITING_DECISION' && stage.key === 'awaiting' && (
              <span className="inline-flex items-center gap-1 text-xs text-warning mt-1"><span className="animate-pulse-soft">●</span> {t('appDetail.actionRequired')}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ApplicationDetailPage;
